import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Tooltip } from 'antd';
import { Navigate, useParams } from 'react-router-dom';
import { useCurrentWorkspace, useDefaultWorkspace } from '@hooks/workspace';
import { toggleWorkspaceCreationModal } from '@redux/actions/modalAction';
import { AssetsList } from '@components/Assets';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useAuth } from '@hooks/useAuth';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import LottieComponent from '@components/Lottie';
import ApprovalsCard from '@pages/Dashboard/components/ApprovalsCard/ApprovalsCard';
import TasksCard from '@pages/Dashboard/components/TasksCard/TasksCard';
import { useOrganization } from '@components/OrganizationBoundary';
import { useAssetsQuery, useAssetsCountQuery } from '@hooks/queries/assets';
import { MagicianIcon } from '@icons';

function DashboardNoWorkspace() {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization(true);
  const buttonDisabled =
    !currentOrganization.entity?.permissions.createWorkspaces;
  return (
    <div className="dashboard_container__welcome">
      <div className="description">
        <div className="icon">
          <MagicianIcon />
        </div>
        <div className="copy">
          <div className="copy__title">Welcome!</div>
          <div className="copy__description">
            To start using StreamWork, you need to create a workspace.
          </div>
        </div>
      </div>
      <Tooltip
        title={
          buttonDisabled
            ? 'You do not have permission to create a workspace. Please contact your organization administrator.'
            : null
        }
        overlayClassName="toggle_favorite_overlay"
      >
        <Button
          disabled={buttonDisabled}
          onClick={() => {
            dispatch(
              toggleWorkspaceCreationModal({ isEditing: false, visible: true })
            );
          }}
          type="primary"
          size="large"
        >
          Create workspace
        </Button>
      </Tooltip>
    </div>
  );
}

function useTrackDashboardTime() {
  const { user } = useAuth(true, 'internal');

  const userId = user.id;
  useEffect(() => {
    let usageTimerStopped = true;
    const startUsageTimer = () => {
      if (!usageTimerStopped) return;
      usageTimerStopped = false;
      MixpanelService.timeEvent(userId, MixpanelEventType.DASHBOARD_USAGE);
    };
    startUsageTimer();
    const stopUsageTimer = () => {
      if (usageTimerStopped) return;
      usageTimerStopped = true;
      MixpanelService.track(userId, MixpanelEventType.DASHBOARD_USAGE);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopUsageTimer(); // Send event when the user switches to the different tab
      } else if (document.visibilityState === 'visible') {
        startUsageTimer(); // Restart timer when the user comes back to the current tab
      }
    };
    const onPageHide = () => {
      stopUsageTimer(); // Send event when the user navigates away, reload or close the page
    };
    document.addEventListener('visibilitychange', onVisibilityChange, {
      capture: true
    });
    window.addEventListener('pagehide', onPageHide, { capture: true });
    const timeoutId = setTimeout(() => {
      // Also send event every N minutes,
      // so we don't need to wait too long till the user leaves the dashboard
      if (document.visibilityState === 'visible') {
        stopUsageTimer();
        startUsageTimer();
      }
    }, 15 * 60 * 1000);
    return () => {
      stopUsageTimer(); // Send event when the user navigates away by react router
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange, {
        capture: true
      });
      window.removeEventListener('pagehide', onPageHide, { capture: true });
    };
  }, [userId]);
}

function DashboardInWorkspace() {
  useTrackDashboardTime();
  const [currentWorkspace] = useCurrentWorkspace(true);
  const assetsQuery = useAssetsQuery({
    workspaceId: currentWorkspace.id,
    orderBy: ['openedAt:DESC'],
    flatten: true,
    isFolder: false,
    isOpened: true
  });
  const assetsCountQuery = useAssetsCountQuery(
    {
      workspaceId: currentWorkspace.id,
      flatten: true,
      isFolder: false,
      isOpened: true
    },
    {
      placeholderData: 25
    }
  );
  const assetsList = useMemo(() => {
    return assetsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [assetsQuery.data]);

  const assetsCount = assetsCountQuery.data ?? 0;
  const assetsToFetch = Math.max(0, assetsCount - assetsList.length);

  return (
    <div className="dashboard_container__main" id="MAIN_LAYOUT">
      <TasksCard />
      <ApprovalsCard />
      <div className="card_container">
        <div className="card_container__header" style={{ border: 'none' }}>
          <span className="copy">
            <LottieComponent className="icon" isLooped={false} view="star" />
            Recently Opened Media
          </span>
        </div>
        <div
          className="card_container__body"
          style={{ height: 'auto', borderTop: '2px solid #161819' }}
        >
          <InfiniteScroll
            loading={assetsQuery.isFetchingNextPage ? 'more' : false}
            next={assetsQuery.fetchNextPage}
            hasMore={assetsQuery.hasNextPage ?? false}
          >
            <AssetsList
              searchQuery=""
              assetsList={assetsList}
              displayAssetsCount={assetsCount}
              isTileView={true}
              page="recent"
              countToFetch={assetsQuery.isFetchingNextPage ? assetsToFetch : 0}
              listLoading={assetsQuery.isLoading}
            />
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const { user } = useAuth(true, 'internal');
  const { workspaceId: workspaceIdParam } = useParams();
  const defaultWorkspace = useDefaultWorkspace(false);
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const [resolved, setResolved] = useState(false);

  const workspaceId =
    workspaceIdParam ?? currentWorkspace?.id ?? defaultWorkspace?.id;

  useEffect(() => {
    if (workspaceId) {
      setResolved(false);
      setCurrentWorkspace(workspaceId).finally(() => setResolved(true));
    }
  }, [workspaceId, setCurrentWorkspace, currentWorkspace?.id, user.id]);

  const needRedirect = !!workspaceId && workspaceId !== workspaceIdParam;

  if (needRedirect) {
    return <Navigate to={workspaceId} replace />;
  }

  if (workspaceIdParam && !currentWorkspace) {
    if (!resolved) return null;
    return <Navigate to="/" replace />;
  }

  return (
    <div className="dashboard_container">
      {!defaultWorkspace && <DashboardNoWorkspace />}
      {!!currentWorkspace && <DashboardInWorkspace />}
    </div>
  );
}

export default Dashboard;
